import { FormFieldErrors } from "@/components/Inputs/FormInputErr/FormFieldErrors.tsx";
import { AcceptedCards } from "@/components/Payment/AcceptedCards/AcceptedCards.tsx";
import { detectCardBrand } from "@/components/Payment/PaymentForm/detectCardBrand.ts";
import { CardBrand } from "@/components/Payment/PaymentForm/enums/CardBrand.ts";
import { PaymentFI } from "@/components/Payment/PaymentForm/enums/PaymentFI.ts";
import { getCardBrandImgURL } from "@/components/Payment/PaymentForm/getCardBrandImgURL.ts";
import { PaymentFormProps } from "@/components/Payment/PaymentForm/interfaces/PaymentFormProps.ts";
import { nameAndSurnameRuleset } from "@/helpers/form/FormRules.ts";
import { ChangeEvent, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";

export const PaymentForm = (props: PaymentFormProps) => {
  const { control, handleSubmitPaymentForm, onSubmitPaymentForm, errors, register, watch } =
    props.paymentFormData;
  const [cardBrand, setCardBrand] = useState<CardBrand>();
  const { t } = useTranslation();

  const watchSameAsBooker = watch(PaymentFI.SameAsBooker);

  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cardBrand = detectCardBrand(value);

    if (cardBrand) setCardBrand(cardBrand);
    else setCardBrand(undefined);
  };

  return (
    <form className="paymentForm" onSubmit={handleSubmitPaymentForm(onSubmitPaymentForm)}>
      <h3 className="u-h6 u-fw-600">{t("paymentForm.title")}</h3>
      <div className="c-be-booking-form__field c-be-booking-form__field--checkbox noMargin">
        <fieldset className="formField">
          <input type="checkbox" {...register(PaymentFI.SameAsBooker)} id={PaymentFI.SameAsBooker} />
          <label htmlFor={PaymentFI.SameAsBooker}>{t(`paymentForm.${PaymentFI.SameAsBooker}`)}</label>
          <FormFieldErrors errors={errors} name={PaymentFI.SameAsBooker} />
        </fieldset>
      </div>
      <div className="paymentFormGrid">
        {watchSameAsBooker ? null : (
          <>
            <fieldset className="formField">
              <label htmlFor={PaymentFI.HolderFirstName}>
                {t(`paymentForm.${PaymentFI.HolderFirstName}`)} *
              </label>
              <input
                className="styledInput holderFirstNameInput"
                type="text"
                placeholder={t("paymentForm.holderFirstNamePlaceholder")}
                {...register(PaymentFI.HolderFirstName, nameAndSurnameRuleset)}
              />
              <FormFieldErrors errors={errors} name={PaymentFI.HolderFirstName} />
            </fieldset>
            <fieldset className="formField">
              <label htmlFor={PaymentFI.HolderLastName}>
                {t(`paymentForm.${PaymentFI.HolderLastName}`)} *
              </label>
              <input
                className="styledInput holderLastNameInput"
                type="text"
                placeholder={t("paymentForm.holderLastNamePlaceholder")}
                {...register(PaymentFI.HolderLastName, nameAndSurnameRuleset)}
              />
              <FormFieldErrors errors={errors} name={PaymentFI.HolderLastName} />
            </fieldset>
          </>
        )}
        <fieldset className="formField cardInputContainer">
          <label className={"styledLabel"} htmlFor={PaymentFI.CardNumber}>
            {t(`paymentForm.${PaymentFI.CardNumber}`)} *
          </label>
          <Controller
            name={PaymentFI.CardNumber}
            control={control}
            render={({ field }) => (
              <div className="cardNumberInnerInputContainer">
                <InputMask
                  {...field}
                  mask="9999 9999 9999 9999"
                  placeholder="1234 1234 1234 1234"
                  id="cardNumber"
                  maskChar={null}
                  className={"styledInput cardNumberInput"}
                  onChange={(e) => {
                    field.onChange(e);
                    handleCardNumberChange(e);
                  }}
                />
                {cardBrand && (
                  <div className="cardImageContainer">
                    <img src={getCardBrandImgURL(cardBrand)} alt={cardBrand} className="cardImage" />
                  </div>
                )}
              </div>
            )}
          />
          <FormFieldErrors errors={errors} name={PaymentFI.CardNumber} />
        </fieldset>
        <fieldset className="formField expiryInputContainer">
          <label className={"styledLabel"} htmlFor={PaymentFI.ExpiryDate}>
            {t(`paymentForm.${PaymentFI.ExpiryDate}`)} *
          </label>
          <Controller
            name={PaymentFI.ExpiryDate}
            control={control}
            render={({ field }) => (
              <InputMask
                {...field}
                mask="99/99"
                placeholder="MM/YY"
                id="expiryDate"
                maskChar=" "
                className="styledInput"
              />
            )}
          />
          <FormFieldErrors errors={errors} name={PaymentFI.ExpiryDate} />
        </fieldset>
      </div>
      <div className="acceptedCardsWrap">
        <AcceptedCards />
      </div>
    </form>
  );
};
